<template>
  <div class="item-view">
    <main-header />

    <!-- Main product detail page (hidden during customization) -->
    <div v-if="!inCustomization">
      <!-- Product Detail Section -->
      <div class="container product-section">
        <div class="product-grid">
          <!-- Gallery Section -->
          <div class="product-gallery">
            <div class="main-image">
              <img :src="selectedImage" :alt="product.name" />
            </div>
            <div class="thumbnail-container">
              <div 
                v-for="(image, index) in thumbnailImages" 
                :key="index"
                class="thumbnail"
                :class="{ active: selectedImageIndex === index }"
                @click="selectImage(index)"
              >
                <img :src="image" :alt="`${product.name} view ${index + 1}`" />
              </div>
            </div>
          </div>

          <!-- Product Info Section -->
          <div class="product-info">
            <!-- Rating -->
            <div class="rating-section">
              <div class="stars">
                <span v-for="n in 5" :key="n" class="star filled">★</span>
              </div>
              <span class="rating-value">5</span>
              <!-- <span class="review-count">(20 Reviews)</span> -->
            </div>

            <!-- Title and Badge -->
            <h1 class="product-title">{{ product.name }}</h1>

            <!-- Price -->
            <div class="price-section">
              <span class="current-price">${{ product.price }}</span>
              <span class="original-price" v-if="product.originalPrice">${{ product.originalPrice }}</span>
              <span class="discount-badge" v-if="product.discount">{{ product.discount }}% OFF</span>
            </div>

            <!-- Description -->
            <p class="product-description">
              {{ product.description }}
            </p>

            <!-- Shipping + Stock Banner -->
            <div class="shipping-stock-banner">
              <div class="shipping-copy">
                <p>Free Standard Shipping Over $49.00</p>
                <p>Shipping Time: 7 - 10 days</p>
                <p class="production-time">Production: 5-8 days</p>
              </div>
              <div class="in-stock">In Stock</div>
            </div>

            <!-- Quantity and Add to Cart -->
            <div class="purchase-section">
              <div class="quantity-selector">
                <button @click="decreaseQuantity" class="qty-btn">−</button>
                <span class="qty-value">{{ quantity }}</span>
                <button @click="increaseQuantity" class="qty-btn">+</button>
              </div>
              <button class="cta-button" @click="startCustomization(0)">
                START CUSTOMIZATION
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Product Variations Section -->
      <section v-if="variationImages.length > 0" class="product-variations">
        <div class="container">
          <div class="variations-grid">
            <div 
              v-for="(image, index) in variationImages" 
              :key="index"
              class="variation-image"
            >
              <img :src="image" :alt="`${product.name} variation ${index + 1}`" />
            </div>
          </div>
        </div>
      </section>
      <!-- Animal Portrait Upgrade Banner -->
      <portrait-upgrade-banner />

      <!-- Reviews and Q&A Section (reused) -->
      <!-- <ReviewsQASection :reviews="reviews" :questions="questions" :testimonials="testimonials" /> -->


      <!-- Related Products Section -->
      <section class="related-products">
        <div class="container">
          <h2>You may also like:</h2>
          <div class="products-grid">
            <div class="product-card" v-for="item in relatedProducts" :key="item.id">
              <div class="product-image">
                <img :src="item.image" :alt="item.name" />
              </div>
              <div class="product-details">
                <h3>{{ item.name }}</h3>
                <div class="product-meta">
                  <span class="price">${{ item.price }}</span>
                  <div class="rating">
                    <span class="star filled">★</span>
                    <span>{{ item.rating }}</span>
                  </div>
                </div>
                <button class="view-details-btn" @click="$router.push('/item/' + item.id)">VIEW DETAILS</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main-footer />
    </div>

    <!-- Customization pages (inline, hide everything else) -->
    <div v-else>
      <!-- Debug: trigger debugRenderState computed -->
      <div style="display:none">{{ debugRenderState }}</div>
      <div v-if="showInfoInput && dataByTyp && dataByTyp.length > 0">
        <adaptive-information-input
          :dataByTyp="dataByTyp"
          :finalNext="onInfoInputComplete"
          @updateName="receivePet"
          @updatePetType="receivePetType"
          @updateInstagram="receiveInsta"
          @selectedfiles="receiveImages"
        />
      </div>
      <custom-level-card
        v-if="!showInfoInput && currentIndex !== null && configurationType === '' && personalizationSets[currentIndex] && personalizationSets[currentIndex].components && personalizationSets[currentIndex].components.length > 0"
        :setConfigurationType="setConfigurationType"
        :productName="personalizationSets[currentIndex].name"
        :showed_image_amount="personalizationSets[currentIndex].currentComponent?.props?.showed_image_amount"
      />
      <component
        v-if="!showInfoInput && currentIndex !== null && configurationType === 'simple' && personalizationSets[currentIndex] && personalizationSets[currentIndex].components && personalizationSets[currentIndex].components.length > 0"
        :is="componentMap[personalizationSets[currentIndex].currentComponent?.id]"
        v-bind="personalizationSets[currentIndex].currentComponent?.props || {}"
        @updateSelection="handleSelection"
        @next="finishCustomization"
        @cancel="cancelCustomization"
      />
      <component
        v-if="!showInfoInput && currentIndex !== null && configurationType === 'custom' && personalizationSets[currentIndex] && personalizationSets[currentIndex].components && personalizationSets[currentIndex].components.length > 0"
        :is="'SimpleConfigurationComponent'"
        @next="finishCustomization"
      />
      <!-- Feature under development message -->
      <div v-if="!showInfoInput && currentIndex !== null && configurationType === 'custom'" class="under-development">
        <div class="development-message">
          <h3>This feature is still under development</h3>
          <p>We're working hard to bring you the best customization experience. Please check back soon!</p>
          <button @click="cancelCustomization" class="back-button">Go Back</button>
        </div>
      </div>
      <!-- Loading/fallback state when customization data is being loaded -->
      <div v-if="!showInfoInput && (currentIndex === null || !personalizationSets.length)" class="loading-state">
        <div class="loading-message">
          <p>Loading customization...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '@/components/MainHeader.vue';
import MainFooter from '@/components/MainFooter.vue';
import ReviewsQASection from '@/components/ReviewsQASection.vue';
import CustomLevelCard from '@/components/home_components/cards/CustomLevelCard.vue';
import ImageSelector from '@/components/home_components/products/ImageSelector.vue';
import ColorSelector from '@/components/home_components/products/ColorSelector.vue';
import QuoteInputor from '@/components/home_components/products/QuoteInputor.vue';
import NoCustomizationNeeded from '@/components/home_components/products/NoCustomizationNeeded.vue';
import ColorSpectrumSelector from '@/components/home_components/products/ColorSpectrumSelector.vue';
import PhoneCaseModelSelector from '@/components/home_components/products/PhoneCaseModelSelector.vue';
import AdaptiveInformationInput from '@/components/home_components/AdaptiveInformationInput.vue';
import PortraitUpgradeBanner from '@/components/product/PortraitUpgradeBanner.vue';
import { uploadImages } from '@/firebase/storageService';
import { auth } from '@/firebase/init';
import { createJourney } from '@/models/JourneyModel';
import { syncJourney, loadJourney } from '@/services/journeyPersistence';
import { processAndUploadItemImages } from '@/services/itemImageUploadService';

export default {
  name: 'ItemView',
  components: {
    MainHeader,
    MainFooter,
    ReviewsQASection,
    CustomLevelCard,
    ImageSelector,
    ColorSelector,
    QuoteInputor,
    ColorSpectrumSelector,
    PhoneCaseModelSelector,
    AdaptiveInformationInput,
    PortraitUpgradeBanner
  },
  data() {
    return {
      // Mapping product names to their collection names
      productToCollection: {
        'Lightbox': 'Everlast',
        'Cupbearer': 'Everlast',
        'Crystal Carving': 'Everlast',
        'Keychain': 'Everlast',
        'Carved Keychain': 'Everlast',
        'Puzzle': 'Artistico',
        'Leaf': 'Artistico',
        'Leaf Bookmark': 'Artistico',
        'Wood Carving': 'Artistico',
        'Notebook': 'Artistico',
        'Painting': 'Artistico',
        'Minimi': 'Accompania',
        'Minimi Keychain': 'Accompania',
        'Totebag': 'Accompania',
        'Bag': 'Accompania',
        'Mirror': 'Accompania',
        'Caseer Phonecase': 'Accompania',
        'Phone Case': 'Accompania',
        'Furbearer': 'Accompania'
      },
      product: {
        id: 1,
        name: 'MINIMI',
        price: 12.00,
        originalPrice: 18.00,
        discount: 25,
        description: 'The Accompania gift box symbolizes the love between pets and their humans that travel through life\'s journey. This thoughtfully curated collection includes a personalized mirror, a versatile totebag, a customized phone case, a Minimi figurine, a Minimi keychain, and a cuddly Furbearer plush.',
        images: [
          'https://via.placeholder.com/500x500/FFE1CF/E37267?text=Product+1',
          'https://via.placeholder.com/500x500/FFE1CF/E37267?text=Product+2',
          'https://via.placeholder.com/500x500/FFE1CF/E37267?text=Product+3',
          'https://via.placeholder.com/500x500/FFE1CF/E37267?text=Product+4'
        ]
      },
      selectedImageIndex: 0,
      quantity: 1,
      reviews: [
        {
          id: 1,
          petName: 'Remi',
          petType: 'Golden Retriever',
          petAge: 4,
          location: 'San Diego, CA',
          avatar: 'https://via.placeholder.com/60x60/FFE1CF/E37267?text=R',
          rating: 5,
          text: 'I cried happy tears opening this. Every item felt like a love letter to my dog. Beautifully made and so thoughtful.'
        },
        {
          id: 2,
          petName: 'Bucky',
          petType: 'British Bulldog',
          petAge: 2,
          location: 'Charleston, SC',
          avatar: 'https://via.placeholder.com/60x60/FFE1CF/E37267?text=B',
          rating: 5,
          text: 'Got this as a surprise from a friend after losing my pup. It\'s comforting, personal, and full of love.'
        },
        {
          id: 3,
          petName: 'Kona',
          petType: 'Tabby Cat',
          petAge: 5,
          location: 'Seattle, WA',
          avatar: 'https://via.placeholder.com/60x60/FFE1CF/E37267?text=K',
          rating: 5,
          text: 'The customization blew me away. The figurine looks just like my cat, whiskers and all. It\'s the perfect keepsake!'
        }
      ],
      questions: [
        {
          id: 1,
          question: 'Can I customize the products with my own pet?',
          answer: 'Yes! You can upload your pet\'s photo and select features to create custom designs for each item.'
        },
        {
          id: 2,
          question: 'Which types of pets can be customized?',
          answer: 'We support dogs, cats, rabbits, birds, and even small pets like ferrets and hedgehogs. Just choose your pet type at your personalization journey!'
        },
        {
          id: 3,
          question: 'How long does customization and delivery take?',
          answer: 'Customization takes 10-25 mins to complete. Shipping typically adds 7–14 days depending on your location.'
        },
        {
          id: 4,
          question: 'What if I have more than one pet?',
          answer: 'Currently, each box supports one pet per order. For multiple pets, you can place separate orders or contact us for a custom request.'
        },
        {
          id: 5,
          question: 'Is this a good gift for someone else?',
          answer: 'Absolutely! It\'s a thoughtful, personalized gift for any pet lover. You can even include a gift message at checkout.'
        }
      ],
      testimonials: [
        {
          id: 1,
          text: "Got this as a surprise from a friend after losing my pup. It's comforting, personal, and full of love.",
          petName: 'Bucky',
          petInfo: 'British Bulldog, Age 2',
          location: 'Charleston, SC'
        },
        {
          id: 2,
          text: "The customization blew me away. The figurine looks just like my cat, whiskers and all.",
          petName: 'Kona',
          petInfo: 'Tabby Cat, Age 5',
          location: 'Seattle, WA'
        },
        {
          id: 3,
          text: "I cried happy tears opening this. Every item felt like a love letter to my dog.",
          petName: 'Remi',
          petInfo: 'Golden Retriever, Age 4',
          location: 'San Diego, CA'
        }
      ],
      relatedProducts: [],
      userEmail: '',
      userQuestion: '',
      // Inline customization state
      loaded: false,
      inCustomization: false,
      currentIndex: null,
      personalizationSets: [],
      configurationType: '',
      customizationStep: 0,
      componentMap: {
        ImageSelector,
        ColorSelector,
        QuoteInputor,
        NoCustomizationNeeded,
        ColorSpectrumSelector,
        PhoneCaseModelSelector
      },
      // Unique journey id used to persist progress; will equal cartItem._id
      journeyId: null,
      // Adaptive info input state
      inputs: [],
      profileType: null, // null for items (they don't have profile types like sets)
      petName: '',
      petType: '',
      instagram: '',
      images: [],
      showInfoInput: true,
      // New journey structure (same as PersonalizationView)
      currentJourney: null
    };
  },
  computed: {
    selectedImage() {
      return this.product.images[this.selectedImageIndex];
    },
    thumbnailImages() {
      // Show only first 5 images in thumbnail carousel
      return this.product.images?.slice(0, 5) || [];
    },
    variationImages() {
      // Show remaining images (last 3) in lower section
      return this.product.images?.slice(5) || [];
    },
    dataByTyp() {
      try {
        // For items, use 'love' as default profile type for input questions
        // (items don't have profile selection, but we need some input config)
        const profileKey = this.profileType || 'love';
        const original = this.inputs?.[profileKey];
        if (!original || !Array.isArray(original)) {
          return [];
        }
        return original
          .filter((item) => item?.props?.question !== 'Enter your pet social media')
          .filter((item) => item?.props?.question !== 'Upload 10 images')
          .filter((item) => item?.props?.disclaimer == null);
      } catch (e) {
        console.warn('dataByTyp error:', e);
        return [];
      }
    },
    // Debug computed to log render conditions
    debugRenderState() {
      const state = {
        inCustomization: this.inCustomization,
        showInfoInput: this.showInfoInput,
        currentIndex: this.currentIndex,
        configurationType: this.configurationType,
        personalizationSetsLength: this.personalizationSets?.length || 0,
        hasCurrentSet: this.currentIndex !== null && !!this.personalizationSets[this.currentIndex],
        hasComponents: this.currentIndex !== null && this.personalizationSets[this.currentIndex]?.components?.length > 0,
        currentComponentId: this.currentIndex !== null ? this.personalizationSets[this.currentIndex]?.currentComponent?.id : null,
        dataByTypLength: this.dataByTyp?.length || 0,
        // Template conditions
        showAdaptiveInput: this.showInfoInput && this.dataByTyp && this.dataByTyp.length > 0,
        showCustomLevelCard: !this.showInfoInput && this.currentIndex !== null && this.configurationType === '' && this.personalizationSets[this.currentIndex]?.components?.length > 0,
        showSimpleComponent: !this.showInfoInput && this.currentIndex !== null && this.configurationType === 'simple' && this.personalizationSets[this.currentIndex]?.components?.length > 0,
        showLoadingState: !this.showInfoInput && (this.currentIndex === null || !this.personalizationSets.length)
      };
      console.log('🔴 [RENDER STATE]', state);
      return state;
    }
  },
  watch: {
    '$route.params.id'(newId) {
      if (newId) {
        // If navigating to a non-journey URL (fresh product), reset state
        const isItemJourney = String(newId).startsWith('item-');
        if (!isItemJourney) {
          // Reset journey state for fresh product
          this.journeyId = null;
          this.currentJourney = null;
          this.images = [];
          this.petName = '';
          this.petType = '';
          this.instagram = '';
          this.inCustomization = false;
          this.showInfoInput = true;
          this.currentIndex = null;
          this.configurationType = '';
          this.customizationStep = 0;
        }
        this.loadProductData();
      }
    }
  },
  async mounted() {
    console.log('🔵 [MOUNTED] Starting mounted hook');
    console.log('🔵 [MOUNTED] Initial state:', {
      inCustomization: this.inCustomization,
      showInfoInput: this.showInfoInput,
      currentIndex: this.currentIndex,
      configurationType: this.configurationType,
      personalizationSetsLength: this.personalizationSets.length,
      journeyId: this.journeyId
    });
    
    const restored = await this.restoreJourneyIfAny();
    console.log('🔵 [MOUNTED] After restoreJourneyIfAny, restored:', restored);
    
    if (!restored) {
      console.log('🔵 [MOUNTED] Not restored, fetching personalization sets...');
      await this.fetchPersonalizationSets();
    }
    
    console.log('🔵 [MOUNTED] After restore/fetch:', {
      inCustomization: this.inCustomization,
      showInfoInput: this.showInfoInput,
      currentIndex: this.currentIndex,
      configurationType: this.configurationType,
      personalizationSetsLength: this.personalizationSets.length
    });
    
    // CRITICAL: Always ensure personalizationSets are loaded even after restore
    // The restored journey might not have fullControlSets, so we need to fetch them
    if (this.personalizationSets.length === 0) {
      console.log('🔵 [MOUNTED] PersonalizationSets empty after restore, fetching...');
      await this.fetchPersonalizationSets();
      console.log('🔵 [MOUNTED] After fetchPersonalizationSets:', this.personalizationSets.length, 'sets');
    }
    
    // If we're in customization mode but currentIndex is null, set it to 0
    if (this.inCustomization && this.currentIndex === null && this.personalizationSets.length > 0) {
      this.currentIndex = 0;
      console.log('🔵 [MOUNTED] Set currentIndex to 0 for customization');
    }
    
    // If showInfoInput is false but we don't have proper customization state, fix it
    if (this.showInfoInput === false && this.personalizationSets.length > 0) {
      if (this.currentIndex === null) {
        this.currentIndex = 0;
        console.log('🔵 [MOUNTED] Fixed currentIndex to 0');
      }
      if (!this.inCustomization) {
        this.inCustomization = true;
        console.log('🔵 [MOUNTED] Fixed inCustomization to true');
      }
      if (this.configurationType === '' && this.personalizationSets[0]?.professional === true) {
        this.configurationType = 'simple';
        console.log('🔵 [MOUNTED] Fixed configurationType to simple');
      }
    }
    
    console.log('🔵 [MOUNTED] Final state:', {
      inCustomization: this.inCustomization,
      showInfoInput: this.showInfoInput,
      currentIndex: this.currentIndex,
      configurationType: this.configurationType,
      personalizationSetsLength: this.personalizationSets.length,
      hasComponents: this.personalizationSets[0]?.components?.length > 0,
      currentComponent: this.personalizationSets[0]?.currentComponent?.id
    });
  },
  async created() {
    // Load product data from individual_products.json first
    await this.loadProductData();
    
    const rawId = this.$route.params.id;
    if (!rawId) {
      this.getInputs();
      return;
    }
    
    // Check if this is a composite ID (item-productId-timestamp) indicating an existing journey
    const isComposite = String(rawId).startsWith('item-');
    
    // Try to load existing journey from Firebase (same as PersonalizationView)
    let existingJourney = null;
    if (isComposite) {
      existingJourney = await loadJourney(rawId);
    }
    
    // Validate that existing journey has actual progress (for items, don't check profileType)
    const hasProgress = existingJourney && (
      (existingJourney.step && existingJourney.step > 0) ||
      (existingJourney.setProducts && existingJourney.setProducts.length > 0) ||
      (existingJourney.images && existingJourney.images.length > 0) ||
      (existingJourney.imageUrls && existingJourney.imageUrls.length > 0) ||
      existingJourney.petName ||
      existingJourney.petType ||
      existingJourney.instagram
    );
    
    if (existingJourney && hasProgress) {
      // Resume existing journey
      this.journeyId = rawId;
      this.currentJourney = existingJourney;
      
      // Restore fields from journey (items don't use profileType)
      this.petName = existingJourney.petName || this.petName;
      this.petType = existingJourney.petType || this.petType;
      this.instagram = existingJourney.instagram || this.instagram;
      
      // Restore images from journey (URLs from Firebase)
      if (existingJourney.imageUrls && existingJourney.imageUrls.length > 0) {
        this.images = existingJourney.imageUrls.map((url, index) => ({
          url: url,
          name: `image-${index}.jpg`,
          size: 0
        }));
      }
      
      // Restore showInfoInput state - if we have pet info, we've passed the info input step
      if (existingJourney.petName || existingJourney.petType || 
          (existingJourney.imageUrls && existingJourney.imageUrls.length > 0)) {
        this.showInfoInput = false;
      }
      
      // Restore inCustomization and other state from fullControlState
      const st = existingJourney.fullControlState || {};
      if (st.inCustomization) {
        this.inCustomization = true;
      }
      if (typeof st.currentIndex === 'number') {
        this.currentIndex = st.currentIndex;
      }
      if (st.configurationType) {
        this.configurationType = st.configurationType;
      }
      if (typeof st.customizationStep === 'number') {
        this.customizationStep = st.customizationStep;
      }
      
      console.log('Resumed existing item journey:', this.journeyId, 'showInfoInput:', this.showInfoInput, 'currentIndex:', this.currentIndex, 'configurationType:', this.configurationType);
    }
    
    // Attempt early restore if route already has an id (for Vuex/fullControlSets)
    await this.restoreJourneyIfAny();
    this.getInputs();
  },
  methods: {
    async loadProductData() {
      try {
        const routeId = this.$route?.params?.id;
        if (!routeId) return;
        
        // Extract the actual product ID from journey ID (e.g., "item-8-1764796364139" -> "8")
        let productId = routeId;
        if (String(routeId).startsWith('item-')) {
          const parts = String(routeId).split('-');
          if (parts.length >= 2) {
            productId = parts[1]; // Get the product ID part
          }
        }
        
        const res = await fetch('/data/individual_products.json');
        const products = await res.json();
        
        // Find the product by id (convert both to string for comparison)
        const productData = products.find(p => String(p.id) === String(productId));
        console.log('Loading product:', productId, '(from route:', routeId, ') Found:', productData?.name);
        
        if (productData) {
          // Map the data to match the expected format
          this.product = {
            id: productData.id,
            name: productData.name,
            price: productData.price,
            originalPrice: productData.oldPrice,
            discount: productData.discount,
            description: productData.description,
            images: productData.images || []
          };
          // Reset selected image index
          this.selectedImageIndex = 0;
          
          // Load related products (exclude current product, only products with images)
          this.relatedProducts = products
            .filter(p => p.id !== productId && p.images && p.images.length > 0)
            .slice(0, 4) // Limit to 4 related products
            .map(p => ({
              id: p.id,
              name: p.name,
              price: p.price,
              rating: p.rating || 4.9,
              image: p.images[0] // Use first image
            }));
        }
      } catch (e) {
        console.error('Error loading product data:', e);
      }
    },
    async getInputs() {
      try {
        const res = await fetch('/data/personalization/informationinput.json');
        const data = await res.json();
        this.inputs = data;
      } catch (e) {
        // ignore
      }
    },
    async restoreJourneyIfAny() {
      try {
        console.log('🟡 [RESTORE] Starting restoreJourneyIfAny');
        const routeId = this.$route?.params?.id;
        if (!routeId) {
          console.log('🟡 [RESTORE] No routeId, returning false');
          return false;
        }
        
        // Check if this is a composite item journey ID
        const isItemJourney = String(routeId).startsWith('item-');
        console.log('🟡 [RESTORE] routeId:', routeId, 'isItemJourney:', isItemJourney);
        
        // First check Vuex for basic journey info
        const jGetter = this.$store?.getters?.getJourneyByProductId;
        const vuexJourney = typeof jGetter === 'function' ? jGetter(routeId) : null;
        console.log('🟡 [RESTORE] vuexJourney:', vuexJourney ? 'found' : 'not found');
        
        // For item journeys, also try loading from the new persistence system
        let firebaseJourney = null;
        if (isItemJourney) {
          // Try new persistence system first (same as PersonalizationView)
          console.log('🟡 [RESTORE] Loading from Firebase...');
          firebaseJourney = await loadJourney(routeId);
          console.log('🟡 [RESTORE] firebaseJourney:', firebaseJourney ? 'found' : 'not found');
          if (firebaseJourney) {
            console.log('🟡 [RESTORE] firebaseJourney.fullControlState:', firebaseJourney.fullControlState);
          }
          
          // If found, restore currentJourney structure
          if (firebaseJourney) {
            this.currentJourney = firebaseJourney;
            this.journeyId = routeId;
            
            // Ensure setProducts exists for item journeys
            if (!this.currentJourney.setProducts || this.currentJourney.setProducts.length === 0) {
              this.currentJourney.setProducts = [{
                name: this.product?.name || 'item',
                isComplete: false,
                personalization: [],
                personalizationType: null
              }];
            }
            
            // Restore fields from journey (items don't use profileType)
            this.petName = firebaseJourney.petName || this.petName;
            this.petType = firebaseJourney.petType || this.petType;
            this.instagram = firebaseJourney.instagram || this.instagram;
            
            // Restore images from journey (URLs from Firebase)
            if (firebaseJourney.imageUrls && firebaseJourney.imageUrls.length > 0) {
              this.images = firebaseJourney.imageUrls.map((url, index) => ({
                url: url,
                name: `image-${index}.jpg`,
                size: 0
              }));
              console.log('Restored images from journey:', this.images.length);
            }
            
            // Restore showInfoInput state - if we have pet info, we've passed the info input step
            if (firebaseJourney.petName || firebaseJourney.petType || 
                (firebaseJourney.imageUrls && firebaseJourney.imageUrls.length > 0)) {
              this.showInfoInput = false;
            }
            
            // Restore inCustomization and other state from fullControlState
            const st = firebaseJourney.fullControlState || {};
            if (st.inCustomization) {
              this.inCustomization = true;
            }
            if (typeof st.currentIndex === 'number') {
              this.currentIndex = st.currentIndex;
            }
            if (st.configurationType) {
              this.configurationType = st.configurationType;
            }
            if (typeof st.customizationStep === 'number') {
              this.customizationStep = st.customizationStep;
            }
            
            console.log('Restored item journey from new persistence:', routeId, 'showInfoInput:', this.showInfoInput, 'currentIndex:', this.currentIndex, 'configurationType:', this.configurationType);
          }
        }
        
        // If no Vuex journey and no Firebase journey, nothing to restore
        if (!vuexJourney && !firebaseJourney) return false;
        if (vuexJourney && vuexJourney.journeyType !== 'item') return false;
        
        // Bind local journey id to route id for subsequent saves
        this.journeyId = routeId;
        
        // Load full journey data from Firebase journeyService (includes fullControlSets)
        if (!firebaseJourney) {
          const { loadJourneyProgress } = await import('@/firebase/journeyService');
          firebaseJourney = await loadJourneyProgress(routeId);
        }
        
        // Use Firebase data if available, otherwise fall back to Vuex
        const j = firebaseJourney || vuexJourney;
        
        // restore sets and state
        let sets = Array.isArray(j.fullControlSets) ? JSON.parse(JSON.stringify(j.fullControlSets)) : [];
        
        // Check if sets have valid components
        const hasValidComponents = sets.length > 0 && sets.some(s => s.components && s.components.length > 0);
        
        // Fallback: if inCustomization was true but sets are missing or have no components, bootstrap from default config
        // Extract ALL personalization data from journey (same as PersonalizationView/FullControl)
        let preSelectedImages = [];
        let preSelectedSocialMedia = null;
        let preSelectedMultiplePets = null;
        let preSelectedOutfit = null;
        let savedText = null;
        let savedColor = null;
        let savedPhoneModel = null;
        
        // Try to get personalization from setProducts first, then fallback to fullControlSets
        let productPersonalization = [];
        if (j.setProducts && j.setProducts.length > 0 && j.setProducts[0].personalization && j.setProducts[0].personalization.length > 0) {
          productPersonalization = j.setProducts[0].personalization;
          console.log('🟡 [RESTORE] Found personalization from setProducts:', productPersonalization.length);
        } else if (sets.length > 0 && sets[0].personalization && sets[0].personalization.length > 0) {
          productPersonalization = sets[0].personalization;
          console.log('🟡 [RESTORE] Found personalization from fullControlSets:', productPersonalization.length);
        }
        
        if (productPersonalization.length > 0) {
          console.log('🟡 [RESTORE] Processing personalization entries:', productPersonalization.length);
          
          for (const entry of productPersonalization) {
            switch (entry.type) {
              case 'imagePersonalization':
                // New merged structure with array of inputs
                console.log('🟡 [RESTORE] Processing imagePersonalization');
                if (entry.input && Array.isArray(entry.input)) {
                  for (const inputItem of entry.input) {
                    if (inputItem.type === 'uploaded' && inputItem.urls && inputItem.urls.length > 0) {
                      // Restore uploaded images
                      preSelectedImages = inputItem.urls
                        .filter(url => url && typeof url === 'string' && url.startsWith('https://firebasestorage'))
                        .map((url, index) => ({ url, name: `uploaded-${index + 1}.jpg`, isUploaded: true }));
                      console.log('🟡 [RESTORE] Found uploaded images:', preSelectedImages.length);
                      
                      // Restore multiple pets and outfit data
                      if (inputItem.multiplePets || inputItem.multiplePetsList?.length > 0) {
                        preSelectedMultiplePets = {
                          multiplePets: inputItem.multiplePets || false,
                          multiplePetsList: inputItem.multiplePetsList || [],
                          portraitStyle: inputItem.portraitStyle || false
                        };
                        console.log('🟡 [RESTORE] Found multiplePets data');
                      }
                      if (inputItem.selectedOutfitName || inputItem.selectedOutfitStyle) {
                        preSelectedOutfit = {
                          selectedOutfitName: inputItem.selectedOutfitName || null,
                          selectedOutfitStyle: inputItem.selectedOutfitStyle || null,
                          portraitStyle: inputItem.portraitStyle ?? null
                        };
                        console.log('🟡 [RESTORE] Found outfit data:', preSelectedOutfit);
                      }
                    } else if (inputItem.type === 'social') {
                      // Restore social media
                      preSelectedSocialMedia = {
                        platform: inputItem.platform || null,
                        username: inputItem.username || null,
                        multiplePets: inputItem.multiplePets || false,
                        multiplePetsList: inputItem.multiplePetsList || [],
                        portraitStyle: inputItem.portraitStyle || false
                      };
                      console.log('🟡 [RESTORE] Found social media:', preSelectedSocialMedia.platform, '@', preSelectedSocialMedia.username);
                      
                      if (inputItem.selectedOutfitName || inputItem.selectedOutfitStyle) {
                        preSelectedOutfit = {
                          selectedOutfitName: inputItem.selectedOutfitName || null,
                          selectedOutfitStyle: inputItem.selectedOutfitStyle || null,
                          portraitStyle: inputItem.portraitStyle ?? null
                        };
                      }
                    }
                  }
                }
                break;
                
              case 'pictures':
                // Old format - array of URLs or object with urls + metadata
                let imageUrls = [];
                if (Array.isArray(entry.input)) {
                  imageUrls = entry.input;
                } else if (entry.input && entry.input.urls) {
                  imageUrls = entry.input.urls;
                  if (entry.input.multiplePets || entry.input.multiplePetsList?.length > 0) {
                    preSelectedMultiplePets = {
                      multiplePets: entry.input.multiplePets || false,
                      multiplePetsList: entry.input.multiplePetsList || [],
                      portraitStyle: entry.input.portraitStyle || false
                    };
                  }
                  if (entry.input.selectedOutfitName || entry.input.selectedOutfitStyle) {
                    preSelectedOutfit = {
                      selectedOutfitName: entry.input.selectedOutfitName || null,
                      selectedOutfitStyle: entry.input.selectedOutfitStyle || null,
                      portraitStyle: entry.input.portraitStyle ?? null
                    };
                  }
                }
                if (imageUrls.length > 0 && preSelectedImages.length === 0) {
                  preSelectedImages = imageUrls
                    .filter(url => url && typeof url === 'string' && url.startsWith('https://firebasestorage'))
                    .map((url, index) => ({ url, name: `uploaded-${index + 1}.jpg`, isUploaded: true }));
                  console.log('🟡 [RESTORE] Found pictures (old format):', preSelectedImages.length);
                }
                break;
                
              case 'social':
                // Old format social media
                if (!preSelectedSocialMedia) {
                  preSelectedSocialMedia = {
                    platform: entry.input?.platform || null,
                    username: entry.input?.username || null,
                    multiplePets: entry.input?.multiplePets || false,
                    multiplePetsList: entry.input?.multiplePetsList || [],
                    portraitStyle: entry.input?.portraitStyle || false
                  };
                  console.log('🟡 [RESTORE] Found social (old format):', preSelectedSocialMedia.platform);
                }
                break;
                
              case 'text':
                savedText = entry.input;
                console.log('🟡 [RESTORE] Found text:', savedText);
                break;
                
              case 'color':
                savedColor = entry.input;
                console.log('🟡 [RESTORE] Found color:', savedColor);
                break;
                
              case 'phoneModel':
                savedPhoneModel = entry.input;
                console.log('🟡 [RESTORE] Found phoneModel:', savedPhoneModel);
                break;
            }
          }
        }
        
        // Fallback: try imageUrls directly on journey
        if (preSelectedImages.length === 0 && j.imageUrls && Array.isArray(j.imageUrls)) {
          preSelectedImages = j.imageUrls
            .filter(url => url && typeof url === 'string' && url.startsWith('https://firebasestorage'))
            .map((url, index) => ({ url, name: `uploaded-${index + 1}.jpg`, isUploaded: true }));
          console.log('🟡 [RESTORE] Found images from imageUrls fallback:', preSelectedImages.length);
        }
        
        console.log('🟡 [RESTORE] Final restore data:', {
          preSelectedImages: preSelectedImages.length,
          preSelectedSocialMedia: !!preSelectedSocialMedia,
          preSelectedMultiplePets: !!preSelectedMultiplePets,
          preSelectedOutfit: !!preSelectedOutfit,
          savedText: !!savedText,
          savedPhoneModel: !!savedPhoneModel,
          savedColor: !!savedColor
        });
        
        if ((!sets || sets.length === 0 || !hasValidComponents) && j.fullControlState?.inCustomization) {
          console.log('🟡 [RESTORE] Sets missing or have no components, fetching from sets2.json...');
          
          // Fetch the actual product configuration from sets2.json to get ALL components
          try {
            const res = await fetch('/data/personalization/sets2.json');
            const data = await res.json();
            
            // Find the collection for this product
            const productName = this.product.name;
            let collectionName = 'Accompania'; // default fallback
            for (const [key, value] of Object.entries(this.productToCollection)) {
              if (key.toLowerCase() === productName.toLowerCase()) {
                collectionName = value;
                break;
              }
            }
            
            const group = data[collectionName];
            const matchedProduct = (group?.products || []).find(p => 
              p && p.name && p.name.toLowerCase() === productName.toLowerCase()
            );
            
            if (matchedProduct && matchedProduct.components && matchedProduct.components.length > 0) {
              console.log('🟡 [RESTORE] Found product config with', matchedProduct.components.length, 'components');
              sets = [{
                ...matchedProduct,
                showComponent: true,
                personalization: [] // Will be populated below
              }];
            } else {
              console.log('🟡 [RESTORE] Product not found in sets2.json, using default ImageSelector');
              // Fallback to single ImageSelector if product not found
              sets = [
                {
                  name: this.product.name,
                  components: [
                    {
                      id: 'ImageSelector',
                      props: {
                        title: 'Select Image for the item:',
                        name: this.product.name,
                        showed_image_amount: 120,
                        imageList: [],
                        maxSelection: 5,
                        maxUpload: 5,
                      },
                    },
                  ],
                },
              ];
            }
          } catch (fetchError) {
            console.error('🟡 [RESTORE] Failed to fetch sets2.json:', fetchError);
            // Fallback to single ImageSelector
            sets = [
              {
                name: this.product.name,
                components: [
                  {
                    id: 'ImageSelector',
                    props: {
                      title: 'Select Image for the item:',
                      name: this.product.name,
                      showed_image_amount: 120,
                      imageList: [],
                      maxSelection: 5,
                      maxUpload: 5,
                    },
                  },
                ],
              },
            ];
          }
        }
        // Ensure all pre-selected data is present for all component types
        for (let s = 0; s < sets.length; s++) {
          const compArr = sets[s]?.components || [];
          
          // Also restore personalization array to the set
          if (!sets[s].personalization) {
            sets[s].personalization = [];
          }
          
          // Rebuild personalization array from restored data
          if (preSelectedImages.length > 0 || preSelectedSocialMedia) {
            // Find or create imagePersonalization entry
            let imgPers = sets[s].personalization.find(p => p.type === 'imagePersonalization');
            if (!imgPers) {
              imgPers = { type: 'imagePersonalization', input: [], timestamp: Date.now() };
              sets[s].personalization.push(imgPers);
            }
            
            if (preSelectedImages.length > 0) {
              const uploadedEntry = {
                type: 'uploaded',
                urls: preSelectedImages.map(img => img.url),
                multiplePets: preSelectedMultiplePets?.multiplePets || false,
                multiplePetsList: preSelectedMultiplePets?.multiplePetsList || [],
                portraitStyle: preSelectedMultiplePets?.portraitStyle || false,
                selectedOutfitName: preSelectedOutfit?.selectedOutfitName || null,
                selectedOutfitStyle: preSelectedOutfit?.selectedOutfitStyle || null
              };
              const existingUploaded = imgPers.input.findIndex(i => i.type === 'uploaded');
              if (existingUploaded >= 0) {
                imgPers.input[existingUploaded] = uploadedEntry;
              } else {
                imgPers.input.push(uploadedEntry);
              }
            }
            
            if (preSelectedSocialMedia) {
              const socialEntry = {
                type: 'social',
                platform: preSelectedSocialMedia.platform,
                username: preSelectedSocialMedia.username,
                multiplePets: preSelectedSocialMedia.multiplePets || false,
                multiplePetsList: preSelectedSocialMedia.multiplePetsList || [],
                portraitStyle: preSelectedSocialMedia.portraitStyle || false,
                selectedOutfitName: preSelectedOutfit?.selectedOutfitName || null,
                selectedOutfitStyle: preSelectedOutfit?.selectedOutfitStyle || null
              };
              const existingSocial = imgPers.input.findIndex(i => i.type === 'social');
              if (existingSocial >= 0) {
                imgPers.input[existingSocial] = socialEntry;
              } else {
                imgPers.input.push(socialEntry);
              }
            }
          }
          
          if (savedText) {
            const textIdx = sets[s].personalization.findIndex(p => p.type === 'text');
            if (textIdx >= 0) {
              sets[s].personalization[textIdx].input = savedText;
            } else {
              sets[s].personalization.push({ type: 'text', input: savedText, timestamp: Date.now() });
            }
          }
          
          if (savedColor) {
            const colorIdx = sets[s].personalization.findIndex(p => p.type === 'color');
            if (colorIdx >= 0) {
              sets[s].personalization[colorIdx].input = savedColor;
            } else {
              sets[s].personalization.push({ type: 'color', input: savedColor, timestamp: Date.now() });
            }
          }
          
          if (savedPhoneModel) {
            const phoneIdx = sets[s].personalization.findIndex(p => p.type === 'phoneModel');
            if (phoneIdx >= 0) {
              sets[s].personalization[phoneIdx].input = savedPhoneModel;
            } else {
              sets[s].personalization.push({ type: 'phoneModel', input: savedPhoneModel, timestamp: Date.now() });
            }
          }
          
          for (let c = 0; c < compArr.length; c++) {
            const compId = compArr[c].id;
            const existingProps = compArr[c].props || {};
            
            if (compId === 'ImageSelector') {
              compArr[c].props = {
                ...existingProps,
                name: sets[s].name,
                // Never show static product images - user must upload their own
                imageList: [],
                // ensure maxSelection prop exists (component uses it)
                maxSelection: typeof existingProps.maxSelection === 'number' ? existingProps.maxSelection : 5,
                // Pass all pre-selected data from previous session
                preSelectedImages: preSelectedImages,
                preSelectedSocialMedia: preSelectedSocialMedia,
                preSelectedMultiplePets: preSelectedMultiplePets,
                preSelectedOutfit: preSelectedOutfit,
              };
            } else if (compId === 'ColorSelector' || compId === 'ColorSpectrumSelector') {
              // Restore saved color to color selector
              if (savedColor) {
                compArr[c].props = {
                  ...existingProps,
                  preSelectedColor: savedColor,
                };
                console.log('🟡 [RESTORE] Set preSelectedColor for', compId, ':', savedColor);
              }
            } else if (compId === 'QuoteInputor') {
              // Restore saved text to quote inputor
              if (savedText) {
                compArr[c].props = {
                  ...existingProps,
                  preSelectedText: savedText,
                };
                console.log('🟡 [RESTORE] Set preSelectedText for QuoteInputor:', savedText);
              }
            } else if (compId === 'PhoneCaseModelSelector') {
              // Restore saved phone model
              if (savedPhoneModel) {
                compArr[c].props = {
                  ...existingProps,
                  preSelectedModel: savedPhoneModel,
                };
                console.log('🟡 [RESTORE] Set preSelectedModel for PhoneCaseModelSelector:', savedPhoneModel);
              }
            }
          }
          if (compArr.length > 0) {
            const stepToRestore = j.fullControlState?.customizationStep || 0;
            const safeStep = Math.min(stepToRestore, compArr.length - 1);
            sets[s].currentComponent = compArr[safeStep];
            console.log('🟡 [RESTORE] Set currentComponent to step', safeStep, 'of', compArr.length, '- Component:', compArr[safeStep]?.id);
          } else {
            sets[s].isComplete = true;
          }
        }
        this.personalizationSets = sets;
        console.log('🟡 [RESTORE] personalizationSets restored:', sets.length, 'sets');
        console.log('🟡 [RESTORE] First set:', sets[0]?.name, 'components:', sets[0]?.components?.length, 'professional:', sets[0]?.professional);
        console.log('🟡 [RESTORE] Components in first set:', sets[0]?.components?.map(c => c.id));
        
        // Also update this.images with the user uploaded images
        if (preSelectedImages.length > 0) {
          this.images = preSelectedImages.map((img, index) => ({
            url: img.url,
            name: `uploaded-${index + 1}.jpg`,
            size: 0
          }));
          console.log('🟡 [RESTORE] Updated this.images with user uploaded images:', this.images.length);
        }
        
        const st = j.fullControlState || {};
        console.log('🟡 [RESTORE] fullControlState from journey:', st);
        
        this.inCustomization = !!st.inCustomization;
        this.currentIndex = typeof st.currentIndex === 'number' ? st.currentIndex : null;
        this.configurationType = st.configurationType || '';
        this.customizationStep = typeof st.customizationStep === 'number' ? st.customizationStep : 0;
        
        console.log('🟡 [RESTORE] After applying fullControlState:', {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
          showInfoInput: this.showInfoInput
        });
        
        // Restore showInfoInput from fullControlState (default to true if not set)
        // If showInfoInput is explicitly false, keep it false
        if (st.showInfoInput === false) {
          this.showInfoInput = false;
          console.log('🟡 [RESTORE] Set showInfoInput to false from fullControlState');
        }
        
        // CRITICAL FIX: If showInfoInput is false but currentIndex is null, 
        // we need to set up the customization state properly
        if (this.showInfoInput === false && this.currentIndex === null && this.personalizationSets.length > 0) {
          this.currentIndex = 0;
          this.inCustomization = true;
          
          // If configurationType is empty but we have a professional product, set it
          if (this.configurationType === '' && this.personalizationSets[0]?.professional === true) {
            this.configurationType = 'simple';
          }
          
          console.log('🟡 [RESTORE] Fixed missing customization state: currentIndex=0, inCustomization=true, configurationType=', this.configurationType);
        }
        
        console.log('🟡 [RESTORE] Final state after restore:', {
          inCustomization: this.inCustomization,
          showInfoInput: this.showInfoInput,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          personalizationSetsLength: this.personalizationSets.length
        });
        
        this.loaded = true;
        return true;
      } catch (e) {
        console.warn('restoreJourneyIfAny failed', e);
        return false;
      }
    },
    selectImage(index) {
      this.selectedImageIndex = index;
    },
    increaseQuantity() {
      this.quantity++;
    },
    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    async fetchPersonalizationSets() {
      try {
        this.loaded = false;
        
        // Safety check: ensure product and product.name exist
        if (!this.product || !this.product.name) {
          console.warn('Product or product name is not available');
          this.loaded = true;
          return;
        }
        
        const res = await fetch('/data/personalization/sets2.json');
        const data = await res.json();
        // Determine the collection based on the current product name (case-insensitive)
        const productName = this.product.name;
        let collectionName = 'Accompania'; // default fallback
        // Find matching collection (case-insensitive)
        for (const [key, value] of Object.entries(this.productToCollection)) {
          if (key.toLowerCase() === productName.toLowerCase()) {
            collectionName = value;
            break;
          }
        }
        console.log(`Product: ${productName} -> Collection: ${collectionName}`);
        const group = data[collectionName];
        // Find the specific product in the collection
        const matchedProduct = (group?.products || []).find(p => 
          p && p.name && p.name.toLowerCase() === productName.toLowerCase()
        );
        
        if (matchedProduct) {
          const p = { ...matchedProduct };
          p.showComponent = true;
          // Initialize current component
          if (p.components && p.components.length > 0) {
            p.currentComponent = p.components[0];
            // Ensure ImageSelector receives initial list
            if (p.currentComponent.id === 'ImageSelector') {
              const existingProps = p.currentComponent.props || {};
              p.currentComponent.props = {
                ...existingProps,
                name: p.name,
                imageList: ([]),
                maxSelection: typeof existingProps.maxSelection === 'number' ? existingProps.maxSelection : 5,
              };
            }
          } else {
            p.isComplete = true;
          }
          this.personalizationSets = [p];
        } else {
          console.warn(`Product ${productName} not found in collection ${collectionName}`);
          this.personalizationSets = [];
        }
        this.loaded = true;
      } catch (e) {
        console.error('Failed to load personalization sets', e);
        this.loaded = true;
      }
    },
    startCustomization(index) {
      if (!this.personalizationSets.length) return;
      if (!this.personalizationSets[index]?.components?.length) {
        alert('This product is not customizable.');
        return;
      }
      this.currentIndex = index;
      // Generate a stable journey id before any persistence
      if (!this.journeyId) {
        this.journeyId = `item-${this.product.id}-${Date.now()}`;
      }
      
      // Create new journey structure if not exists (same as PersonalizationView)
      if (!this.currentJourney) {
        const user = auth.currentUser;
        this.currentJourney = createJourney(
          this.journeyId,
          user ? user.uid : 'anonymous',
          'item', // journeyType is 'item' for single products
          this.product.name
        );
        // For item journeys, setProducts contains only this one item
        this.currentJourney.setProducts = [{
          name: this.product.name,
          isComplete: false,
          personalization: [],
          personalizationType: null
        }];
        
        // Clear any cached images for new journey
        this.images = [];
        this.petName = '';
        this.petType = '';
        this.instagram = '';
        
        console.log('Created new item journey:', this.journeyId);
      }
      
      // Ensure URL reflects the journey id for resume/refresh
      try {
        const currentId = this.$route?.params?.id;
        if (currentId !== this.journeyId) {
          this.$router.replace(`/item/${this.journeyId}`);
        }
      } catch (e) {
        // ignore navigation errors
      }
      // For professional flow, set configuration type without persisting yet
      if (this.personalizationSets[index].professional === true) {
        this.setConfigurationType('simple', false);
      }
      this.inCustomization = true;
      this.showInfoInput = true;
      window.scrollTo(0, 0);
      
      // Update journey step and sync
      if (this.currentJourney) {
        this.currentJourney.step = 1;
        this.syncJourneyData();
      }
      
      // persist journey start
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product?.name || 'Unknown Product',
        step: 1,
        isComplete: false,
        journeyType: 'item',
        fullControlState: {
          inCustomization: this.inCustomization,
          showInfoInput: this.showInfoInput,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
        },
      });
    },
    async syncJourneyData() {
      if (!this.currentJourney) return;
      
      try {
        // Update journey metadata
        this.currentJourney.updatedAt = Date.now();
        
        // Sync to cookies and Firebase (same as PersonalizationView)
        await syncJourney(this.currentJourney);
        console.log('Item journey synced:', this.journeyId);
      } catch (error) {
        console.error('Error syncing item journey:', error);
      }
    },
    onInfoInputComplete() {
      this.showInfoInput = false;
      
      // Update journey structure
      if (this.currentJourney) {
        this.currentJourney.petName = this.petName;
        this.currentJourney.petType = this.petType;
        this.currentJourney.instagram = this.instagram;
        this.currentJourney.step = 2;
        // Save fullControlState with showInfoInput: false
        this.currentJourney.fullControlState = {
          ...(this.currentJourney.fullControlState || {}),
          inCustomization: this.inCustomization,
          showInfoInput: false,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
        };
        this.syncJourneyData();
      }
      
      // persist that we collected info fields
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        petName: this.petName,
        petType: this.petType,
        instagram: this.instagram,
        images: this.images,
        fullControlState: {
          inCustomization: this.inCustomization,
          showInfoInput: false,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
        },
      });
      window.scrollTo(0, 0);
    },
    receivePet(name) {
      this.petName = name;
      
      // Update journey structure
      if (this.currentJourney) {
        this.currentJourney.petName = name;
        this.syncJourneyData();
      }
      
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        petName: this.petName,
      });
    },
    receivePetType(type) {
      this.petType = type;
      
      // Update journey structure
      if (this.currentJourney) {
        this.currentJourney.petType = type;
        this.syncJourneyData();
      }
      
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        petType: this.petType,
      });
    },
    receiveInsta(handle) {
      this.instagram = handle;
      
      // Update journey structure
      if (this.currentJourney) {
        this.currentJourney.instagram = handle;
        this.syncJourneyData();
      }
      
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        instagram: this.instagram,
      });
    },
    async receiveImages(files) {
      console.log('🔵 [ITEM VIEW] receiveImages called');
      console.log('🔵 [ITEM VIEW] Files received:', files.length);
      console.log('🔵 [ITEM VIEW] Journey ID:', this.journeyId);
      console.log('🔵 [ITEM VIEW] Product:', this.product.name);
      
      try {
        // Store File objects temporarily
        this.images = files;
        console.log('✅ [ITEM VIEW] Files stored locally');
        
        const user = auth.currentUser;
        if (!user) {
          console.warn('⚠️ [ITEM VIEW] User not logged in, cannot upload images');
          this.$store?.dispatch('set_journey_progress', {
            productId: this.journeyId,
            productName: this.product.name,
            journeyType: 'item',
            images: this.images,
          });
          return;
        }
        
        console.log('🔵 [ITEM VIEW] User authenticated:', user.uid);
        console.log('🔵 [ITEM VIEW] Starting Firebase Storage upload...');
        
        // Upload to Firebase Storage
        const imageUrls = await uploadImages(files, user.uid, this.journeyId);
        
        console.log('✅ [ITEM VIEW] Firebase upload complete');
        console.log('🔵 [ITEM VIEW] Image URLs:', imageUrls);
        
        // Store both File objects and URLs
        this.images = files.map((file, index) => ({
          file: file,
          url: imageUrls[index],
          name: file.name,
          size: file.size
        }));
        
        console.log('✅ [ITEM VIEW] Images array updated with URLs');
        
        // Update journey structure with image URLs (same as PersonalizationView)
        if (this.currentJourney) {
          // Store image URLs in the journey
          this.currentJourney.imageUrls = imageUrls;
          
          // Also update the single product's personalization with the images
          if (this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
            this.currentJourney.setProducts[0].personalization = [{
              type: 'pictures',
              input: imageUrls,
              timestamp: Date.now()
            }];
          }
          
          this.syncJourneyData();
          console.log('✅ [ITEM VIEW] Journey structure updated with image URLs');
        }
        
        console.log('🔵 [ITEM VIEW] Persisting journey progress to Vuex/Firebase...');
        
        this.$store?.dispatch('set_journey_progress', {
          productId: this.journeyId,
          productName: this.product.name,
          journeyType: 'item',
          images: this.images,
          imageUrls: imageUrls
        });
        
        console.log('✅ [ITEM VIEW] Journey progress persisted');
      } catch (error) {
        console.error('❌ [ITEM VIEW] Error uploading images:', error);
        console.error('❌ [ITEM VIEW] Error details:', error.message);
        console.error('❌ [ITEM VIEW] Error stack:', error.stack);
        this.images = files;
        this.$store?.dispatch('set_journey_progress', {
          productId: this.journeyId,
          productName: this.product.name,
          journeyType: 'item',
          images: this.images,
        });
      }
    },
    setConfigurationType(type, persist = true) {
      this.configurationType = type;
      if (!persist) return;
      // persist config change
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        step: this.customizationStep + 1,
        isComplete: false,
        journeyType: 'item',
        fullControlSets: this.personalizationSets,
        fullControlState: {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
        },
      });
    },
    finishCustomization() {
      console.log('🔵 [ITEM VIEW] finishCustomization called');
      console.log('🔵 [ITEM VIEW] Current step:', this.customizationStep);
      
      const currentSet = this.personalizationSets[this.currentIndex];
      const componentsLength = currentSet?.components?.length || 0;
      console.log('🔵 [ITEM VIEW] Total steps:', componentsLength);
      
      if (!currentSet || !currentSet.components) {
        console.warn('⚠️ [ITEM VIEW] No current set or components');
        return;
      }
      
      if (this.customizationStep >= componentsLength - 1) {
        console.log('✅ [ITEM VIEW] Final step reached - completing customization');
        
        this.personalizationSets[this.currentIndex].isComplete = true;
        this.customizationStep = 0;
        this.personalizationSets[this.currentIndex].currentComponent = this.personalizationSets[this.currentIndex].components[0];
        
        // add to cart and complete journey
        try {
          console.log('🔵 [ITEM VIEW] Preparing cart item...');
          
          // Ensure journeyId exists and use it for cart and journey linkage
          const id = this.journeyId || `item-${this.product.id}-${Date.now()}`;
          this.journeyId = id;
          
          console.log('🔵 [ITEM VIEW] Journey ID:', id);
          console.log('🔵 [ITEM VIEW] Product:', this.product.name);
          console.log('🔵 [ITEM VIEW] Price:', this.product.price);
          console.log('🔵 [ITEM VIEW] Images array:', this.images);
          
          // Extract image URLs from uploaded images
          const imageUrls = this.images.map(img => img.url || img).filter(Boolean);
          
          console.log('🔵 [ITEM VIEW] Extracted image URLs:', imageUrls);
          console.log('🔵 [ITEM VIEW] Product catalog images:', this.product.images);
          
          const cartItem = {
            _id: id,
            productId: id,
            brand: this.product.name,
            product: this.product.name,
            price: this.product.price,
            currency: '$',
            quantity: 1,
            images: this.product.images || [],
            uploadedImages: this.images,
            imageUrls: imageUrls,
            petName: this.petName,
            petType: this.petType,
            instagram: this.instagram
          };
          
          console.log('🔵 [ITEM VIEW] Cart item prepared:', JSON.stringify(cartItem, (key, value) => {
            if (value instanceof File) return '[File object]';
            return value;
          }, 2));
          
          console.log('🔵 [ITEM VIEW] Dispatching add_to_cart...');
          this.$store?.dispatch('add_to_cart', cartItem);
          
          console.log('🔵 [ITEM VIEW] Dispatching complete_journey...');
          this.$store?.dispatch('complete_journey', this.journeyId);
        } catch (e) {
          console.error('❌ [ITEM VIEW] Failed to add to cart/complete journey:', e);
          console.error('❌ [ITEM VIEW] Error details:', e.message);
          console.error('❌ [ITEM VIEW] Error stack:', e.stack);
        }
        
        console.log('🔵 [ITEM VIEW] Persisting final journey state...');
        
        // Update journey structure to complete (same as PersonalizationView)
        if (this.currentJourney) {
          this.currentJourney.status = 'FINISHED';
          this.currentJourney.isComplete = true;
          this.currentJourney.completedAt = Date.now();
          if (this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
            this.currentJourney.setProducts[0].isComplete = true;
          }
          this.syncJourneyData();
          console.log('✅ [ITEM VIEW] Journey structure marked complete');
        }
        
        // persist final state before exit
        this.$store?.dispatch('set_journey_progress', {
          productId: this.journeyId,
          productName: this.product.name,
          step: 0,
          isComplete: true,
          journeyType: 'item',
          fullControlSets: this.personalizationSets,
          fullControlState: {
            inCustomization: false,
            currentIndex: null,
            configurationType: '',
            customizationStep: 0,
          },
        });
        
        console.log('✅ [ITEM VIEW] Final journey state persisted');
        
        // exit customization
        this.inCustomization = false;
        this.currentIndex = null;
        window.scrollTo(0, 0);
        this.setConfigurationType('', false);
        
        console.log('🔵 [ITEM VIEW] Navigating to cart...');
        // navigate to cart to match existing mechanism
        this.$router.push('/cart');
        
        console.log('✅ [ITEM VIEW] finishCustomization complete');
      } else {
        this.customizationStep++;
        this.personalizationSets[this.currentIndex].currentComponent = this.personalizationSets[this.currentIndex].components[this.customizationStep];
        if (this.personalizationSets[this.currentIndex].currentComponent.id === 'ImageSelector') {
          const existingProps = this.personalizationSets[this.currentIndex].currentComponent.props || {};
          this.personalizationSets[this.currentIndex].currentComponent.props = {
            ...existingProps,
            name: this.personalizationSets[this.currentIndex].name,
            imageList: (this.product.images || []).map((u) => ({ url: u })),
            maxSelection: typeof existingProps.maxSelection === 'number' ? existingProps.maxSelection : 5,
          };
        }
        // persist journey progress per step
        this.$store?.dispatch('set_journey_progress', {
          productId: this.journeyId,
          productName: this.product.name,
          step: this.customizationStep + 1,
          isComplete: false,
          journeyType: 'item',
          fullControlSets: this.personalizationSets,
          fullControlState: {
            inCustomization: this.inCustomization,
            currentIndex: this.currentIndex,
            configurationType: this.configurationType,
            customizationStep: this.customizationStep,
          },
        });
      }
    },
    cancelCustomization() {
      // If we're on step > 0, go back to the previous step instead of canceling completely
      if (this.customizationStep > 0 && this.currentIndex !== null && this.personalizationSets[this.currentIndex]?.components?.length > 1) {
        console.log('🔵 [ITEM VIEW] Going back to previous step:', this.customizationStep - 1);
        this.customizationStep--;
        const currentSet = this.personalizationSets[this.currentIndex];
        currentSet.currentComponent = currentSet.components[this.customizationStep];
        
        // If going back to ImageSelector, restore the preSelectedImages from personalization data
        if (currentSet.currentComponent?.id === 'ImageSelector') {
          const preSelectedImages = this.getPreSelectedImagesFromPersonalization(currentSet);
          const preSelectedSocialMedia = this.getPreSelectedSocialMediaFromPersonalization(currentSet);
          
          currentSet.currentComponent.props = {
            ...currentSet.currentComponent.props,
            preSelectedImages: preSelectedImages,
            preSelectedSocialMedia: preSelectedSocialMedia,
            imageList: [], // Don't show static images
          };
          console.log('🔵 [ITEM VIEW] Restored preSelectedImages for ImageSelector:', preSelectedImages.length);
        }
        
        // Persist the step change
        this.$store?.dispatch('set_journey_progress', {
          productId: this.journeyId,
          productName: this.product.name,
          step: this.customizationStep,
          isComplete: false,
          journeyType: 'item',
          fullControlSets: this.personalizationSets,
          fullControlState: {
            inCustomization: this.inCustomization,
            currentIndex: this.currentIndex,
            configurationType: this.configurationType,
            customizationStep: this.customizationStep,
            showInfoInput: this.showInfoInput
          },
        });
        return;
      }
      
      // If we're on step 0, go back to configuration type selection (not pet info)
      // Only show pet info again if user explicitly wants to start over
      if (this.customizationStep === 0 && this.configurationType !== '') {
        console.log('🔵 [ITEM VIEW] Going back to configuration type selection');
        this.setConfigurationType('', false);
        // Stay in customization mode, just reset the configuration type
        // Persist the state
        this.$store?.dispatch('set_journey_progress', {
          productId: this.journeyId,
          productName: this.product.name,
          step: 0,
          isComplete: false,
          journeyType: 'item',
          fullControlSets: this.personalizationSets,
          fullControlState: {
            inCustomization: this.inCustomization,
            currentIndex: this.currentIndex,
            configurationType: '',
            customizationStep: this.customizationStep,
            showInfoInput: this.showInfoInput
          },
        });
        return;
      }
      
      // If configurationType is already empty (at config selection), go back to product page
      console.log('🔵 [ITEM VIEW] Going back to product page');
      this.inCustomization = false;
      this.customizationStep = 0;
      if (this.currentIndex !== null && this.personalizationSets[this.currentIndex]?.components?.length) {
        this.personalizationSets[this.currentIndex].currentComponent = this.personalizationSets[this.currentIndex].components[0];
      }
      this.currentIndex = null;
      // Don't reset showInfoInput - keep it false so user doesn't have to re-enter pet info
      // this.showInfoInput = true;  // REMOVED - don't show pet info again
      window.scrollTo(0, 0);
      this.setConfigurationType('', false);
      
      // Navigate back to the product page (not pet info)
      this.$router.push(`/item/${this.product.id}`);
    },
    async handleSelection(selection) {
      console.log('🔵 [ITEM VIEW] handleSelection called');
      console.log('🔵 [ITEM VIEW] Selection data:', selection);
      
      // Store currentIndex at the start since it may change during async operations
      const savedCurrentIndex = this.currentIndex;
      const savedJourneyId = this.journeyId;
      const savedProductName = this.product?.name || 'item';
      
      if (savedCurrentIndex === null) {
        console.warn('⚠️ [ITEM VIEW] currentIndex is null, skipping selection');
        return;
      }
      
      // Check if personalizationSets exists
      if (!this.personalizationSets || !this.personalizationSets[savedCurrentIndex]) {
        console.warn('⚠️ [ITEM VIEW] personalizationSets not available, skipping selection');
        return;
      }
      
      const currentSet = this.personalizationSets[savedCurrentIndex];
      const currentComponent = currentSet.currentComponent;
      
      if (!currentComponent) {
        console.warn('⚠️ [ITEM VIEW] No current component, skipping selection');
        return;
      }
      
      console.log('🔵 [ITEM VIEW] Current component:', currentComponent.id);
      
      // Route to appropriate handler based on component type (same as FullControl)
      
      // Handle ImageSelector component (both uploaded images and social media)
      if (currentComponent.id === 'ImageSelector' && selection && typeof selection === 'object') {
        if (selection.type === 'uploaded' || selection.type === 'social') {
          console.log('🖼️ [ITEM VIEW] ImageSelector detected, handling image/social selection');
          await this.handleImageSelection(selection);
          return;
        }
      }
      
      // Handle text input - QuoteInputor emits the text directly as a string
      if (currentComponent.id === 'QuoteInputor' && typeof selection === 'string') {
        console.log('📝 [ITEM VIEW] QuoteInputor detected, handling text selection');
        this.handleTextSelection(selection);
        return;
      }
      
      // Handle color selection
      // ColorSelector emits an array: ["#FF5733"]
      // ColorSpectrumSelector emits a string: "#E6F4EA"
      if (currentComponent.id === 'ColorSelector' && Array.isArray(selection)) {
        console.log('🎨 [ITEM VIEW] ColorSelector detected, handling color selection');
        const color = selection[0];
        if (color) {
          this.handleColorSelection(color);
        }
        return;
      } else if (currentComponent.id === 'ColorSpectrumSelector' && typeof selection === 'string' && selection.startsWith('#')) {
        console.log('🎨 [ITEM VIEW] ColorSpectrumSelector detected, handling color selection');
        this.handleColorSelection(selection);
        return;
      }
      
      // Handle phone model selection - PhoneCaseModelSelector emits object: {brand, model}
      if (currentComponent.id === 'PhoneCaseModelSelector' && selection && selection.brand !== undefined) {
        console.log('📱 [ITEM VIEW] PhoneCaseModelSelector detected, handling phone model selection');
        this.handlePhoneModelSelection(selection);
        return;
      }
      
    },
    /**
     * Handle image selection from ImageSelector (uploaded images or social media)
     */
    async handleImageSelection(selection) {
      console.log('🖼️ [ITEM VIEW] handleImageSelection called');
      console.log('   Selection type:', selection.type);
      
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle image selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      
      // Initialize personalization array if not exists
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Find or create imagePersonalization entry
      let imagePersonalization = currentSet.personalization.find(p => p.type === 'imagePersonalization');
      if (!imagePersonalization) {
        imagePersonalization = {
          type: 'imagePersonalization',
          input: [],
          timestamp: Date.now()
        };
        currentSet.personalization.push(imagePersonalization);
      }
      
      // Handle uploaded images
      if (selection.type === 'uploaded' && selection.images && selection.images.length > 0) {
        try {
          const user = auth.currentUser;
          if (!user) {
            console.error('❌ [ITEM VIEW] User not logged in, cannot upload images');
            return;
          }
          
          console.log('🔵 [ITEM VIEW] Uploading images to Firebase Storage...');
          
          // Use processAndUploadItemImages to properly upload to Firebase Storage
          const imageUrls = await processAndUploadItemImages(
            selection.images,
            this.journeyId,
            this.product?.name || 'item'
          );
          
          console.log('✅ [ITEM VIEW] Images uploaded to Firebase Storage:', imageUrls);
          
          // Filter out any blob URLs or placeholder URLs
          const validUrls = imageUrls.filter(url => 
            url && 
            typeof url === 'string' && 
            !url.startsWith('blob:') && 
            !url.includes('placeholder')
          );
          
          console.log('✅ [ITEM VIEW] Valid Firebase URLs:', validUrls);
          
          // Update this.images array with Firebase URLs
          this.images = validUrls.map((url, index) => ({
            url: url,
            name: `image-${index + 1}.jpg`,
            size: 0
          }));
          
          // Build uploaded input object (same format as FullControl)
          const uploadedInput = {
            type: 'uploaded',
            urls: validUrls,
            multiplePets: selection.multiplePets || false,
            multiplePetsList: selection.multiplePetsList || [],
            portraitStyle: selection.portraitStyle || false,
            selectedOutfitName: selection.selectedOutfitName || null,
            selectedOutfitStyle: selection.selectedOutfitStyle || null,
            timestamp: Date.now()
          };
          
          // Update or add uploaded entry in imagePersonalization
          const existingUploadedIndex = imagePersonalization.input.findIndex(i => i.type === 'uploaded');
          if (existingUploadedIndex >= 0) {
            imagePersonalization.input[existingUploadedIndex] = uploadedInput;
          } else {
            imagePersonalization.input.push(uploadedInput);
          }
          
          console.log('✅ [ITEM VIEW] Saved uploaded images to personalization');
          
          // Update journey structure with Firebase image URLs
          if (this.currentJourney) {
            this.currentJourney.imageUrls = validUrls;
            if (this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
              this.currentJourney.setProducts[0].personalization = currentSet.personalization;
            }
            this.syncJourneyData();
          }
        } catch (error) {
          console.error('❌ [ITEM VIEW] Error uploading images:', error);
        }
      }
      
      // Handle social media selection
      if (selection.type === 'social') {
        const socialInput = {
          type: 'social',
          platform: selection.platform || '',
          username: selection.username || '',
          multiplePets: selection.multiplePets || false,
          multiplePetsList: selection.multiplePetsList || [],
          portraitStyle: selection.portraitStyle || false,
          selectedOutfitName: selection.selectedOutfitName || null,
          selectedOutfitStyle: selection.selectedOutfitStyle || null,
          timestamp: Date.now()
        };
        
        // Update or add social entry in imagePersonalization
        const existingSocialIndex = imagePersonalization.input.findIndex(i => i.type === 'social');
        if (existingSocialIndex >= 0) {
          imagePersonalization.input[existingSocialIndex] = socialInput;
        } else {
          imagePersonalization.input.push(socialInput);
        }
        
        console.log('✅ [ITEM VIEW] Saved social media to personalization:', socialInput);
        
        // Update journey structure
        if (this.currentJourney) {
          if (this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
            this.currentJourney.setProducts[0].personalization = currentSet.personalization;
          }
          this.syncJourneyData();
        }
      }
      
      // Persist to Firebase immediately (include setProducts for restore)
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product?.name || 'item',
        journeyType: 'item',
        fullControlSets: this.personalizationSets,
        setProducts: [{
          name: this.product?.name || 'item',
          personalization: currentSet.personalization
        }],
        fullControlState: {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
          showInfoInput: this.showInfoInput
        }
      });
    },
    /**
     * Handle text selection (same as FullControl)
     */
    handleTextSelection(text) {
      console.log('🔵 [ITEM VIEW] handleTextSelection called:', text);
      
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle text selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      const itemName = currentSet.name || this.product.name;
      
      // Initialize personalization array if not exists
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Check if text entry already exists
      const textIndex = currentSet.personalization.findIndex(p => p.type === 'text');
      const textEntry = {
        type: 'text',
        input: text,
        timestamp: Date.now()
      };
      
      if (textIndex >= 0) {
        currentSet.personalization[textIndex] = textEntry;
      } else {
        currentSet.personalization.push(textEntry);
      }
      
      console.log(`✅ [ITEM VIEW] Saved text for ${itemName}:`, text);
      
      // Update journey structure
      if (this.currentJourney && this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
        this.currentJourney.setProducts[0].personalization = currentSet.personalization;
        this.syncJourneyData();
      }
      
      // Persist to Firebase (include setProducts for restore)
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        fullControlSets: this.personalizationSets,
        setProducts: [{
          name: this.product.name,
          personalization: currentSet.personalization
        }],
        fullControlState: {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
          showInfoInput: this.showInfoInput
        }
      });
    },
    /**
     * Handle color selection (same as FullControl)
     */
    handleColorSelection(color) {
      console.log('🔵 [ITEM VIEW] handleColorSelection called:', color);
      
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle color selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      const itemName = currentSet.name || this.product.name;
      
      // Initialize personalization array if not exists
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Check if color entry already exists
      const colorIndex = currentSet.personalization.findIndex(p => p.type === 'color');
      const colorEntry = {
        type: 'color',
        input: color,
        timestamp: Date.now()
      };
      
      if (colorIndex >= 0) {
        currentSet.personalization[colorIndex] = colorEntry;
      } else {
        currentSet.personalization.push(colorEntry);
      }
      
      console.log(`✅ [ITEM VIEW] Saved color for ${itemName}:`, color);
      
      // Update journey structure
      if (this.currentJourney && this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
        this.currentJourney.setProducts[0].personalization = currentSet.personalization;
        this.syncJourneyData();
      }
      
      // Persist to Firebase (include setProducts for restore)
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        fullControlSets: this.personalizationSets,
        setProducts: [{
          name: this.product.name,
          personalization: currentSet.personalization
        }],
        fullControlState: {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
          showInfoInput: this.showInfoInput
        }
      });
    },
    /**
     * Handle phone model selection (same as FullControl)
     */
    handlePhoneModelSelection(modelData) {
      console.log('🔵 [ITEM VIEW] handlePhoneModelSelection called:', modelData);
      
      if (this.currentIndex === null || !this.personalizationSets || !this.personalizationSets[this.currentIndex]) {
        console.error('Cannot handle phone model selection: invalid state');
        return;
      }
      
      const currentSet = this.personalizationSets[this.currentIndex];
      const itemName = currentSet.name || this.product.name;
      
      // Initialize personalization array if not exists
      if (!currentSet.personalization) {
        currentSet.personalization = [];
      }
      
      // Check if phoneModel entry already exists
      const phoneModelIndex = currentSet.personalization.findIndex(p => p.type === 'phoneModel');
      const phoneModelEntry = {
        type: 'phoneModel',
        input: modelData, // {brand: "iPhone", model: "iPhone 15 Pro"}
        timestamp: Date.now()
      };
      
      if (phoneModelIndex >= 0) {
        currentSet.personalization[phoneModelIndex] = phoneModelEntry;
      } else {
        currentSet.personalization.push(phoneModelEntry);
      }
      
      console.log(`✅ [ITEM VIEW] Saved phone model for ${itemName}:`, modelData);
      
      // Update journey structure
      if (this.currentJourney && this.currentJourney.setProducts && this.currentJourney.setProducts.length > 0) {
        this.currentJourney.setProducts[0].personalization = currentSet.personalization;
        this.syncJourneyData();
      }
      
      // Persist to Firebase (include setProducts for restore)
      this.$store?.dispatch('set_journey_progress', {
        productId: this.journeyId,
        productName: this.product.name,
        journeyType: 'item',
        fullControlSets: this.personalizationSets,
        setProducts: [{
          name: this.product.name,
          personalization: currentSet.personalization
        }],
        fullControlState: {
          inCustomization: this.inCustomization,
          currentIndex: this.currentIndex,
          configurationType: this.configurationType,
          customizationStep: this.customizationStep,
          showInfoInput: this.showInfoInput
        }
      });
    },
    /**
     * Extract preSelectedImages from personalization data
     */
    getPreSelectedImagesFromPersonalization(set) {
      if (!set || !set.personalization) return [];
      
      // Check imagePersonalization (new format)
      const imgPers = set.personalization.find(p => p.type === 'imagePersonalization');
      if (imgPers && imgPers.input && Array.isArray(imgPers.input)) {
        const uploadedEntry = imgPers.input.find(i => i.type === 'uploaded');
        if (uploadedEntry && uploadedEntry.urls && uploadedEntry.urls.length > 0) {
          return uploadedEntry.urls
            .filter(url => url && typeof url === 'string' && url.startsWith('https://firebasestorage'))
            .map((url, index) => ({ url, name: `uploaded-${index + 1}.jpg`, isUploaded: true }));
        }
      }
      
      // Check pictures (old format)
      const picturesEntry = set.personalization.find(p => p.type === 'pictures');
      if (picturesEntry) {
        let urls = [];
        if (Array.isArray(picturesEntry.input)) {
          urls = picturesEntry.input;
        } else if (picturesEntry.input && picturesEntry.input.urls) {
          urls = picturesEntry.input.urls;
        }
        if (urls.length > 0) {
          return urls
            .filter(url => url && typeof url === 'string' && url.startsWith('https://firebasestorage'))
            .map((url, index) => ({ url, name: `uploaded-${index + 1}.jpg`, isUploaded: true }));
        }
      }
      
      // Fallback to this.images if available
      if (this.images && this.images.length > 0) {
        return this.images
          .filter(img => img.url && img.url.startsWith('https://firebasestorage'))
          .map((img, index) => ({ url: img.url, name: `uploaded-${index + 1}.jpg`, isUploaded: true }));
      }
      
      return [];
    },
    /**
     * Extract preSelectedSocialMedia from personalization data
     */
    getPreSelectedSocialMediaFromPersonalization(set) {
      if (!set || !set.personalization) return null;
      
      // Check imagePersonalization (new format)
      const imgPers = set.personalization.find(p => p.type === 'imagePersonalization');
      if (imgPers && imgPers.input && Array.isArray(imgPers.input)) {
        const socialEntry = imgPers.input.find(i => i.type === 'social');
        if (socialEntry && socialEntry.platform) {
          return {
            platform: socialEntry.platform,
            username: socialEntry.username || '',
            multiplePets: socialEntry.multiplePets || false,
            multiplePetsList: socialEntry.multiplePetsList || [],
            portraitStyle: socialEntry.portraitStyle || false
          };
        }
      }
      
      // Check social (old format)
      const socialEntry = set.personalization.find(p => p.type === 'social');
      if (socialEntry && socialEntry.input && socialEntry.input.platform) {
        return {
          platform: socialEntry.input.platform,
          username: socialEntry.input.username || '',
          multiplePets: socialEntry.input.multiplePets || false,
          multiplePetsList: socialEntry.input.multiplePetsList || [],
          portraitStyle: socialEntry.input.portraitStyle || false
        };
      }
      
      return null;
    },
    submitQuestion() {
      if (this.userEmail && this.userQuestion) {
        console.log('Question submitted:', this.userEmail, this.userQuestion);
        // Handle question submission
        this.userEmail = '';
        this.userQuestion = '';
      }
    }
  }
  ,
  async beforeRouteUpdate(to, from, next) {
    // On route change, try restoring item journey state
    await this.restoreJourneyIfAny();
    next();
  }
};
</script>

<style scoped>
.item-view {
  background-color: #fffef9;
  min-height: 100vh;
}

.container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 60px;
}

/* Product Section */
.product-section {
  padding: 40px 60px;
}

.product-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 60px;
  margin-top: 40px;
}

.product-gallery {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.main-image {
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1; /* square by default, scales with width */
  max-height: 500px; /* cap on large screens */
  background: #ffeee5;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* crop image to fit square */
  max-width: 100%;
}

.thumbnail-container {
  display: flex;
  gap: 15px;
  margin-top: 20px;
  justify-content: center;
}

.thumbnail {
  width: 100px;
  height: 100px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  transition: border-color 0.3s;
}

.thumbnail.active {
  border-color: #d78d86;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Product Info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.rating-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stars {
  display: flex;
  gap: 2px;
}

.star {
  color: #ccc;
  font-size: 18px;
}

.star.filled {
  color: #e37267;
}

.rating-value {
  font-weight: 600;
  font-size: 16px;
  color: #000;
}

.review-count {
  color: #989898;
  font-size: 14px;
}

.product-title {
  font-family: 'Jost', sans-serif;
  font-size: 30px;
  font-weight: 600;
  color: #000;
  margin: 10px 0;
}

.discount-badge {
  background: #ffe9dd;
  color: #e37267;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
  width: fit-content;
}

.price-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.current-price {
  font-size: 18px;
  font-weight: 600;
  color: #000;
}

.original-price {
  font-size: 14px;
  color: #d78d86;
  text-decoration: line-through;
}

.product-description {
  font-size: 12px;
  line-height: 1.5;
  color: #989898;
}

/* Shipping + Stock Banner */
.shipping-stock-banner {
  --shipping-copy-size: 1.35rem;
  --shipping-stock-size: 1.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff1ea;
  border-radius: 0.5rem;
  padding: 1.125rem 1.5rem;
}

.shipping-copy {
  color: #6f6f6f;
}

.shipping-copy p {
  margin: 0;
  font-size: var(--shipping-copy-size);
  line-height: 1.2;
  color: #6f6f6f;
}

.shipping-copy .production-time {
  font-weight: 600;
  color: #e37267;
}

.in-stock {
  color: #e37267;
  font-weight: 800;
  font-size: var(--shipping-stock-size);
}

@media (max-width: 768px) {
  .shipping-stock-banner {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    --shipping-copy-size: 1.15rem;
    --shipping-stock-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .shipping-stock-banner {
    --shipping-copy-size: 1rem;
    --shipping-stock-size: 1.3rem;
  }
}

.purchase-section {
  display: flex;
  gap: 20px;
  align-items: center;
}

.quantity-selector {
  display: flex;
  align-items: center;
  background: #e37267;
  border-radius: 20px;
  overflow: hidden;
  width: 130px;
  height: 46px;
}

.qty-btn {
  width: 40px;
  height: 100%;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qty-value {
  flex: 1;
  text-align: center;
  color: #d78d86;
  font-weight: 700;
  font-size: 18px;
  background: #fffef9;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 3px 0;
}

.cta-button {
  flex: 1;
  background: #e37267;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 2px 3px 2px 0px rgba(168, 98, 91, 0.9);
  transition: background-color 0.3s;
}

.cta-button:hover {
  background: #c85b50;
}

/* Features Section */
.features-section {
  background: #ffeee5;
  padding: 80px 0;
  margin-top: 60px;
}

.section-title {
  font-family: 'Eczar', serif;
  font-size: 48px;
  color: #e37267;
  text-align: center;
  margin-bottom: 20px;
  text-transform: uppercase;
  line-height: 1.2;
}

.section-subtitle {
  font-size: 16px;
  font-weight: 700;
  color: black;
  text-align: center;
}
.section-title::after {
  content: 'Whether for a playful pup, a curious cat, or a chatty parrot, this thoughtfully crafted gift is more than just a treat — it’s a token of your bond. ';
  display: block;
  margin-top: 10px;
  font-size: 14px;
  color: #989898;
  line-height: 1.5;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
}

.feature-card {
  text-align: center;
}

.feature-card img {
  width: 100%;
  height: 350px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 18px;
  color: #e37267;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.feature-card p {
  font-size: 12px;
  color: #7e7e7e;
  line-height: 1.5;
}

/* Reviews Section */
.reviews-section {
  padding: 80px 0;
}

.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.reviews-header h2 {
  font-size: 24px;
  font-weight: 700;
  color: #e37267;
}

.reviews-summary {
  display: flex;
  align-items: center;
  gap: 15px;
}

.rating {
  font-size: 16px;
  font-weight: 600;
  color: #000;
}

.based-on {
  font-size: 11px;
  color: #000;
}

.rating-breakdown {
  margin-bottom: 40px;
  padding: 20px;
  background: #ffeee5;
  border-radius: 10px;
}

.rating-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.rating-bar {
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.rating-fill {
  height: 100%;
  background: #e37267;
  transition: width 0.3s;
}

.write-review-btn {
  background: #e37267;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  margin-top: 20px;
}

.reviews-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.review-card {
  background: transparent;
  padding: 20px;
}

.review-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.pet-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}

.review-meta h4 {
  font-size: 12px;
  font-weight: 700;
  color: #000;
}

.pet-info,
.location {
  font-size: 10px;
  color: #908c8c;
  margin: 2px 0;
}

.review-rating {
  margin-bottom: 15px;
}

.review-text {
  font-size: 12px;
  color: #000;
  line-height: 1.5;
}

/* Q&A Section */
.qa-section {
  padding: 60px 0;
  background: #fffef9;
}

.qa-section h2 {
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin-bottom: 30px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e37267;
}

.qa-list {
  margin-bottom: 40px;
}

.qa-item {
  margin-bottom: 25px;
}

.question {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.q-icon {
  width: 19px;
  height: 19px;
  background-size: contain;
  flex-shrink: 0;
}

.question p {
  font-size: 14px;
  font-weight: 700;
  color: #000;
}

.answer {
  font-size: 12px;
  color: #000;
  line-height: 1.5;
  margin-left: 29px;
}

.ask-question {
  padding: 20px;
  border: 1px solid #d78d86;
  border-radius: 5px;
}

.ask-question h3 {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin-bottom: 20px;
}

.question-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.question-form input,
.question-form textarea {
  padding: 10px;
  border: 1px solid #e37267;
  border-radius: 5px;
  font-size: 12px;
  color: #d78d86;
  background: #fffef9;
}

.question-form input::placeholder,
.question-form textarea::placeholder {
  color: #d78d86;
}

.question-form textarea {
  min-height: 80px;
  resize: vertical;
}

.question-form button {
  width: fit-content;
  background: #e37267;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 30px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 2px 3px 2px 0px rgba(168, 98, 91, 0.9);
}

/* Product Variations */
.product-variations {
  padding: 60px 0;
  background: #fffef9;
}

.variations-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.variation-image {
  width: 100%;
  height: 0;
  padding-top: 100%;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  box-shadow: 0px 4px 4px 0px rgba(168, 98, 91, 0.9);
}

.variation-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Related Products */
.related-products {
  padding: 60px 0;
  background: #fffef9;
}

.related-products h2 {
  font-size: 24px;
  font-weight: 800;
  color: #e37267;
  margin-bottom: 40px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
}

.product-card {
  background: rgba(255, 255, 255, 0);
  overflow: hidden;
}
.product-image {
  width: 100%;
  height: 0;
  padding-top: 100%; /* 关键：使高度 = 宽度 */
  border-radius: 5px;
  box-shadow: 0px 4px 4px 0px rgba(168, 98, 91, 0.9);
  position: relative;
  overflow: hidden;
}

.product-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-details {
  position: relative;
  padding: 15px;
}

.product-details h3 {
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 10px;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.product-meta .price {
  font-size: 14px;
  color: #000;
}

.product-meta .rating {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 600;
}

.view-details-btn {
  position: absolute;
  bottom: 140px;
  left: 0;
  width: 100%;
  background: #e37267;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 2px 3px 2px 0px rgba(168, 98, 91, 0.9);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .container {
    padding: 0 30px;
  }

  .product-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .product-section {
    padding: 30px 20px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .reviews-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .variations-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .product-section {
    padding: 20px 10px;
  }

  .section-title {
    font-size: 32px;
  }

  .product-title {
    font-size: 24px;
  }

  .purchase-section {
    flex-direction: column;
    gap: 15px;
  }

  .quantity-selector,
  .cta-button {
    width: 100%;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .variations-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
}

/* Under Development Message Styles */
.under-development {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.development-message {
  text-align: center;
  max-width: 600px;
  background: #fffef9;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.development-message h3 {
  font-size: 24px;
  color: #e37267;
  margin-bottom: 16px;
  font-weight: 600;
}

.development-message p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 24px;
}

.back-button {
  padding: 12px 32px;
  background-color: #e37267;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 2px 3px 2px 0px rgba(168, 98, 91, 0.9);
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #d45d52;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(227, 114, 103, 0.4);
}

/* Loading State Styles */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.loading-message {
  text-align: center;
  max-width: 600px;
  background: #fffef9;
  padding: 40px;
  border-radius: 10px;
}

.loading-message p {
  font-size: 18px;
  color: #666;
}
</style>
